import * as PIXI from "pixi.js";
import TickerTimeout from "../../utils/TickerTimeout";
import FXManager from "../../Engine/Effects/FXManager";
import {
  computeArc,
  EASING_FNS,
} from "../../Engine/Collection/BezierPath";
import { Settings } from "../../settings";
import { FONT_STYLES, scaleStyle } from "../../fontStyles";

/**
 * Local quadratic-Bézier sampler — same math as Engine/Collection `evaluatePath`,
 * but writes into a caller-owned `out` point instead of allocating a new {x,y}
 * every frame per traveler. Kept PC-local so the shared evaluatePath util
 * (also used by RoyalCollect) stays untouched.
 */
function _samplePathInto(pathData, t, out) {
  const ct = Math.max(0, Math.min(1, t));
  const inv = 1 - ct;
  out.x = inv * inv * pathData.start.x + 2 * inv * ct * pathData.control.x + ct * ct * pathData.end.x;
  out.y = inv * inv * pathData.start.y + 2 * inv * ct * pathData.control.y + ct * ct * pathData.end.y;
  return out;
}

/**
 * PotCollectionValueTraveler
 *
 * Creates PIXI.Text clones of coin values and animates them along a Bézier arc
 * to a destination position (e.g., Collect Zeus). Used by the Collect feature
 * (GDD §15.3) and Double feature trails (GDD §15.2).
 *
 * Supports two modes:
 *   - `travel()` — bare text Bézier (no trail, backward compat)
 *   - `travelWithTrail()` — text + FXManager lightning trail following the container
 *     (same pattern as RC's CoinCollectAnimator)
 *
 * Pattern: fire-and-forget. Each traveler self-destructs on arrival.
 */
class PotCollectionValueTraveler {
  /**
   * @param {PIXI.Container} container - Parent container for travelers
   * @param {number} [symbolWidth] - Current symbol cell width (for arc computation)
   * @param {number} [symbolHeight] - Current symbol cell height (for arc computation)
   * @param {Function} [getDimensions] - Optional callback returning {symbolWidth, symbolHeight} from live source
   */
  constructor(container, symbolWidth, symbolHeight, getDimensions) {
    this.container = container;
    this.symbolWidth = symbolWidth || 200;
    this.symbolHeight = symbolHeight || 240;
    this._getDimensions = getDimensions || null;
    this._activeTravelers = [];
    this._cancelled = false;
    this._pendingTimers = new Set(); // Timer leak prevention
  }

  /**
   * Sync cached dimensions from the live getDimensions callback.
   * Called at the start of each travel and on each ticker frame.
   */
  _syncDimensions() {
    if (!this._getDimensions) return;
    const dims = this._getDimensions();
    if (dims?.symbolWidth) this.symbolWidth = dims.symbolWidth;
    if (dims?.symbolHeight) this.symbolHeight = dims.symbolHeight;
  }

  /**
   * Animate a text value from source to destination along a Bézier arc (no trail).
   * Backward-compatible with existing FeatureExecutor usage.
   *
   * @param {Object} params
   * @param {string} params.text - Display text (e.g., "500")
   * @param {PIXI.TextStyle} params.style - PIXI.TextStyle for the clone
   * @param {{x: number, y: number}} params.from - Start position (container-local)
   * @param {{x: number, y: number}} params.to - End position (container-local)
   * @param {number} [params.duration=600] - Duration in ms
   * @param {number} [params.arcHeight=0.3] - Arc height as fraction of distance
   * @param {Function} [params.onArrival] - Called when text arrives
   * @returns {Promise<void>}
   */
  travel({ text, style, from, to, duration = 600, arcHeight = 0.3, onArrival }) {
    this._syncDimensions();
    if (this._cancelled || !this.container || this.container.destroyed) {
      onArrival?.();
      return Promise.resolve();
    }

    // Create text clone
    const textObj = new PIXI.Text(text, style);
    textObj.anchor.set(0.5);
    textObj.position.set(from.x, from.y);
    textObj.zIndex = 100;

    // Auto-fit: shrink text if wider than the symbol cell
    const paddingPercent = 0.05;
    const maxTextWidth = this.symbolWidth * (1 - paddingPercent * 2);
    if (textObj.width > maxTextWidth) {
      const displayValue = textObj.text || "";
      const digitCount = displayValue.replace(/[^0-9]/g, "").length;
      const scaleRatio = maxTextWidth / textObj.width;
      if (digitCount <= 8) {
        textObj.scale.x = scaleRatio;
      } else {
        textObj.scale.set(scaleRatio);
      }
    }

    this.container.addChild(textObj);

    // Calculate Bézier control point (arc apex)
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const controlPoint = {
      x: midX - dy * arcHeight, // Perpendicular offset for arc
      y: midY - dist * arcHeight,
    };

    let elapsed = 0;
    let lastSymbolHeight = this.symbolHeight; // Track for live resize

    return new Promise((resolve) => {
      const entry = { textObj, tickerFn: null, resolve, trailEmitter: null };

      entry.tickerFn = () => {
        if (this._cancelled || textObj.destroyed) {
          PIXI.Ticker.shared.remove(entry.tickerFn);
          resolve();
          return;
        }

        // Live font resize: sync from overlay controllers then check for change
        this._syncDimensions();
        if (this.symbolHeight !== lastSymbolHeight) {
          lastSymbolHeight = this.symbolHeight;
          const newFontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          Object.assign(textObj.style, scaleStyle(FONT_STYLES.coinValue, newFontSize));
        }

        elapsed += PIXI.Ticker.shared.deltaMS;
        const t = Math.min(1, elapsed / duration);

        // Quadratic Bézier: B(t) = (1-t)²·P0 + 2(1-t)t·P1 + t²·P2
        const oneMinusT = 1 - t;
        const x =
          oneMinusT * oneMinusT * from.x +
          2 * oneMinusT * t * controlPoint.x +
          t * t * to.x;
        const y =
          oneMinusT * oneMinusT * from.y +
          2 * oneMinusT * t * controlPoint.y +
          t * t * to.y;

        textObj.position.set(x, y);

        // Scale: grow slightly mid-arc, shrink at end
        const scalePulse = 1 + 0.15 * Math.sin(t * Math.PI);
        textObj.scale.set(scalePulse);

        // Alpha: fade in fast, fade out at end
        if (t < 0.1) {
          textObj.alpha = t / 0.1;
        } else if (t > 0.8) {
          textObj.alpha = (1 - t) / 0.2;
        } else {
          textObj.alpha = 1;
        }

        if (t >= 1) {
          PIXI.Ticker.shared.remove(entry.tickerFn);
          entry.tickerFn = null;

          const idx = this._activeTravelers.indexOf(entry);
          if (idx !== -1) this._activeTravelers.splice(idx, 1);

          if (textObj.parent) textObj.parent.removeChild(textObj);
          textObj.destroy();

          onArrival?.();
          resolve();
        }
      };

      this._activeTravelers.push(entry);
      PIXI.Ticker.shared.add(entry.tickerFn);
    });
  }

  /**
   * Animate a cloned value text along a Bézier arc WITH a particle_trail particle trail.
   * Mirrors RC CoinCollectAnimator._animateSingleItem:
   *   - Same Bézier arc (computeArc with 0.7 arcHeight)
   *   - Same easeInOut easing
   *   - Same 500ms canonical duration
   *   - Trail_fx "trail" emitter follows the wrapper each frame
   *
   * The text is a CLONE — the original value stays visible on the source coin
   * (GDD §15.3.6: source coins KEEP their values).
   *
   * @param {Object} params
   * @param {string} params.text - Display text (e.g., "500")
   * @param {PIXI.TextStyle} params.style - PIXI.TextStyle for the clone
   * @param {{x: number, y: number}} params.from - Start position (container-local)
   * @param {{x: number, y: number}} params.to - End position (container-local)
   * @param {number} [params.duration=500] - Duration in ms (matches RC COIN_TRAVEL_DURATION)
   * @param {Function} [params.onArrival] - Called when text arrives at destination
   * @param {number} [params.scaleFrom] - Starting scale (1.0 = natural size)
   * @param {number} [params.scaleTo] - Ending scale (interpolated during travel)
   * @returns {Promise<void>}
   */
  travelWithTrail({ text, style, from, to, duration = 500, onArrival, displayObject, scaleFrom, scaleTo }) {
    this._syncDimensions();
    if (this._cancelled || !this.container || this.container.destroyed) {
      onArrival?.();
      return Promise.resolve();
    }

    // Create wrapper container (same as RC's itemContainer)
    const wrapper = new PIXI.Container();
    wrapper.name = "value_traveler_trail";
    wrapper.position.set(from.x, from.y);
    wrapper.zIndex = 200;

    if (displayObject) {
      // Use provided display object (e.g., cloned 2x spine) instead of text
      wrapper.addChild(displayObject);
    } else {
      // Create cloned text inside wrapper
      const textObj = new PIXI.Text(text, style);
      textObj.anchor.set(0.5);
      textObj.zIndex = 100;
      textObj.resolution = 2;
      textObj.roundPixels = true;
      wrapper.addChild(textObj);

      // Auto-fit: shrink text uniformly if wider than the symbol cell
      const paddingPercent = 0.05;
      const maxTextWidth = this.symbolWidth * (1 - paddingPercent * 2);
      if (textObj.width > maxTextWidth) {
        const scaleRatio = maxTextWidth / textObj.width;
        textObj.scale.set(scaleRatio);
      }
    }

    this.container.addChild(wrapper);


    // Start trail — same pattern as PotChargingAnimator.fireTrail:
    //   emitter.init(parentContainer) renders particles into parent
    //   emitter.target = wrapper — RevoltFX auto-follows wrapper.x/y each frame
    //   As wrapper moves, particles spawn at new position → trail behind it
    let trailEmitter = null;
    if (FXManager.isTrailReady) {
      const trailScale = (this.symbolHeight / 266) * 1.0;
      trailEmitter = FXManager.emitTrail(
        this.container,
        wrapper,
        { scale: trailScale },
      );
    }

    // Normalize positions for resize resilience (RC line 256-259)
    const captureW = this.symbolWidth;
    const captureH = this.symbolHeight;
    const normStart = { x: from.x / captureW, y: from.y / captureH };
    const normEnd = { x: to.x / captureW, y: to.y / captureH };

    const easingFn = EASING_FNS.easeInOut;
    let elapsed = 0;
    let lastSymbolWidth = this.symbolWidth;
    let lastSymbolHeight = this.symbolHeight; // Track for live resize

    // Pre-compute path (only recomputed on resize)
    let cachedStart = { x: normStart.x * lastSymbolWidth, y: normStart.y * lastSymbolHeight };
    let cachedEnd = { x: normEnd.x * lastSymbolWidth, y: normEnd.y * lastSymbolHeight };
    let cachedPath = computeArc(cachedStart, cachedEnd, lastSymbolHeight, { arcHeightMultiplier: 0.5 });

    // Reused output point for the per-frame path sample — avoids allocating a
    // new {x,y} every frame. Owned by this call, so concurrent trails don't share it.
    const posOut = { x: 0, y: 0 };

    // Same data shape as RC travelerData (line 264)
    const travelerData = { container: wrapper, tickerFn: null, trailEmitter };

    return new Promise((resolve) => {
      travelerData.tickerFn = () => {
        if (this._cancelled || wrapper.destroyed) {
          PIXI.Ticker.shared.remove(travelerData.tickerFn);
          travelerData.tickerFn = null;
          if (travelerData.trailEmitter) {
            travelerData.trailEmitter.target = null;
            FXManager.killTrail(travelerData.trailEmitter);
            travelerData.trailEmitter = null;
          }
          const idx = this._activeTravelers.indexOf(travelerData);
          if (idx !== -1) this._activeTravelers.splice(idx, 1);
          if (!wrapper.destroyed) {
            wrapper.destroy({ children: true });
          }
          resolve();
          return;
        }

        // Sync live dimensions from overlay controllers, then recompute if changed
        this._syncDimensions();
        if (this.symbolWidth !== lastSymbolWidth || this.symbolHeight !== lastSymbolHeight) {
          lastSymbolWidth = this.symbolWidth;
          lastSymbolHeight = this.symbolHeight;
          cachedStart = { x: normStart.x * lastSymbolWidth, y: normStart.y * lastSymbolHeight };
          cachedEnd = { x: normEnd.x * lastSymbolWidth, y: normEnd.y * lastSymbolHeight };
          cachedPath = computeArc(cachedStart, cachedEnd, lastSymbolHeight, { arcHeightMultiplier: 0.5 });

          // Live font resize
          if (!displayObject) {
            const textChild = wrapper.children[0];
            if (textChild && !textChild.destroyed && textChild instanceof PIXI.Text) {
              const newFontSize = lastSymbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
              Object.assign(textChild.style, scaleStyle(FONT_STYLES.coinValue, newFontSize));
            }
          } else {
            // Live resize for display objects (e.g., x2 label spine)
            // Spine was created at this.symbolHeight / nativeHeight scale — rescale to match new dims
            const nativeHeight = Settings.SYMBOL_HEIGHT || 266;
            const newScale = lastSymbolHeight / nativeHeight;
            const child = wrapper.children[0];
            if (child && !child.destroyed) {
              // If child is a shield container, rescale the spine inside it
              const target = child.children?.[0] || child;
              if (target && !target.destroyed) target.scale.set(newScale);
            }
          }
        }

        elapsed += PIXI.Ticker.shared.deltaMS;
        const rawT = Math.min(elapsed / duration, 1);
        const easedT = easingFn(rawT);

        // Evaluate cached path into the reused point (no allocations)
        const pos = _samplePathInto(cachedPath, easedT, posOut);

        // Position along curve (RC line 286)
        wrapper.position.set(pos.x, pos.y);

        // Scale interpolation (for 2x label shrink during travel)
        if (scaleFrom != null && scaleTo != null) {
          const s = scaleFrom + (scaleTo - scaleFrom) * easedT;
          wrapper.scale.set(s);
        }

        // Complete (RC line 288-305)
        if (rawT >= 1) {
          PIXI.Ticker.shared.remove(travelerData.tickerFn);
          travelerData.tickerFn = null;

          const idx = this._activeTravelers.indexOf(travelerData);
          if (idx !== -1) this._activeTravelers.splice(idx, 1);

          // Stop trail — prevent read-after-destroy, then let particles fade
          // (matches PotChargingAnimator.fireTrail cleanup)
          if (trailEmitter) {
            trailEmitter.target = null;
            FXManager.stopTrail(trailEmitter);
            trailEmitter = null;
          }

          // Destroy the text container (RC line 302-303)
          wrapper.destroy({ children: true });

          onArrival?.();
          resolve();
        }
      };

      this._activeTravelers.push(travelerData);
      PIXI.Ticker.shared.add(travelerData.tickerFn);
    });
  }

  // ── Grand Jackpot Trail (Two-Leg Arc with Hold) ─────────────────────────────

  /**
   * Animate the Grand Jackpot value in two legs using the SAME arc shape
   * as regular coin/collect trails (computeArc + evaluatePath):
   *
   *   Leg 1: from → via  (arc + trail, scaleFrom → scalePeak)
   *   Hold:  pause at via for holdDuration at scalePeak
   *   Leg 2: via → to    (arc + trail, scalePeak → scaleTo)
   *
   * A single wrapper/text is created and reused across both legs — no flash.
   * Trail stops between legs for dramatic hold, then restarts for leg 2.
   *
   * @param {Object} params
   * @param {string} params.text - Display text (e.g., "5,000")
   * @param {PIXI.TextStyle} params.style - Base PIXI.TextStyle
   * @param {{x: number, y: number}} params.from - Start position (GJP label, container-local)
   * @param {{x: number, y: number}} params.to - End position (collector, container-local)
   * @param {{x: number, y: number}} params.via - Midpoint (grid center, container-local)
   * @param {number} [params.leg1Duration=400] - Leg 1 duration in ms
   * @param {number} [params.holdDuration=300] - Hold at via in ms
   * @param {number} [params.leg2Duration=400] - Leg 2 duration in ms
   * @param {number} [params.scaleFrom=1] - Scale at start
   * @param {number} [params.scalePeak=4] - Scale at via (held)
   * @param {number} [params.scaleTo=0.5] - Scale at end (collector counter size)
   * @param {Function} [params.onArrival] - Called when text arrives at collector
   * @returns {Promise<void>}
   */
  travelTwoLeg({ text, style, from, to, via, leg1Duration = 400, holdDuration = 300, leg2Duration = 400, scaleFrom = 1, scalePeak = 4, scaleTo = 0.5, onArrival }) {
    this._syncDimensions();
    if (this._cancelled || !this.container || this.container.destroyed) {
      onArrival?.();
      return Promise.resolve();
    }

    // Create wrapper container (reused across both legs)
    const wrapper = new PIXI.Container();
    wrapper.name = "gjp_value_traveler";
    wrapper.position.set(from.x, from.y);
    wrapper.zIndex = 300; // Above regular value travelers (200)

    const textObj = new PIXI.Text(text, new PIXI.TextStyle(style));
    textObj.anchor.set(0.5);
    textObj.zIndex = 100;
    textObj.resolution = Math.ceil(scalePeak); // Match peak scale so text stays crisp at max zoom
    textObj.roundPixels = true;
    wrapper.addChild(textObj);

    // Auto-fit: shrink text if wider than symbol cell
    const paddingPercent = 0.05;
    const maxTextWidth = this.symbolWidth * (1 - paddingPercent * 2);
    if (textObj.width > maxTextWidth) {
      const scaleRatio = maxTextWidth / textObj.width;
      textObj.scale.set(scaleRatio);
    }

    this.container.addChild(wrapper);

    // Shared state for the traveler entry (for cancel/cleanup)
    const travelerData = { container: wrapper, tickerFn: null, trailEmitter: null };
    this._activeTravelers.push(travelerData);

    const self = this;

    // ── Helper: animate one leg (same arc as travelWithTrail) ──
    const animateLeg = (legFrom, legTo, duration, legScaleFrom, legScaleTo) => {
      return new Promise((resolve) => {
        if (self._cancelled || wrapper.destroyed) { resolve(); return; }

        // Start trail for this leg
        let trailEmitter = null;
        if (FXManager.isTrailReady) {
          const trailScale = (self.symbolHeight / 266) * 1.0;
          trailEmitter = FXManager.emitTrail(
            self.container,
            wrapper,
            { scale: trailScale },
          );
        }
        travelerData.trailEmitter = trailEmitter;

        // Normalize positions for resize resilience (same as travelWithTrail)
        const captureW = self.symbolWidth;
        const captureH = self.symbolHeight;
        const normFrom = { x: legFrom.x / captureW, y: legFrom.y / captureH };
        const normTo = { x: legTo.x / captureW, y: legTo.y / captureH };

        let lastSymbolWidth = captureW;
        let lastSymbolHeight = captureH;

        // Pre-compute arc path (recomputed on resize)
        let cachedStart = { x: legFrom.x, y: legFrom.y };
        let cachedEnd = { x: legTo.x, y: legTo.y };
        let cachedPath = computeArc(cachedStart, cachedEnd, lastSymbolHeight, { arcHeightMultiplier: 0.5 });
        const easingFn = EASING_FNS.easeInOut;
        let elapsed = 0;
        const posOut = { x: 0, y: 0 }; // reused per-frame sample point (no alloc)

        travelerData.tickerFn = () => {
          if (self._cancelled || wrapper.destroyed) {
            PIXI.Ticker.shared.remove(travelerData.tickerFn);
            travelerData.tickerFn = null;
            if (trailEmitter) {
              trailEmitter.target = null;
              FXManager.killTrail(trailEmitter);
              travelerData.trailEmitter = null;
            }
            resolve();
            return;
          }

          // Sync + recompute arc on resize (same pattern as travelWithTrail)
          self._syncDimensions();
          if (self.symbolWidth !== lastSymbolWidth || self.symbolHeight !== lastSymbolHeight) {
            lastSymbolWidth = self.symbolWidth;
            lastSymbolHeight = self.symbolHeight;
            cachedStart = { x: normFrom.x * lastSymbolWidth, y: normFrom.y * lastSymbolHeight };
            cachedEnd = { x: normTo.x * lastSymbolWidth, y: normTo.y * lastSymbolHeight };
            cachedPath = computeArc(cachedStart, cachedEnd, lastSymbolHeight, { arcHeightMultiplier: 0.5 });
          }

          elapsed += PIXI.Ticker.shared.deltaMS;
          const rawT = Math.min(elapsed / duration, 1);
          const easedT = easingFn(rawT);

          // Position along arc (same sampler as travelWithTrail)
          const pos = _samplePathInto(cachedPath, easedT, posOut);
          wrapper.position.set(pos.x, pos.y);

          // Scale interpolation
          const s = legScaleFrom + (legScaleTo - legScaleFrom) * easedT;
          wrapper.scale.set(s);

          // Complete this leg
          if (rawT >= 1) {
            PIXI.Ticker.shared.remove(travelerData.tickerFn);
            travelerData.tickerFn = null;

            // Stop trail — let particles fade naturally
            if (trailEmitter) {
              trailEmitter.target = null;
              FXManager.stopTrail(trailEmitter);
              travelerData.trailEmitter = null;
            }

            resolve();
          }
        };

        PIXI.Ticker.shared.add(travelerData.tickerFn);
      });
    };

    // ── Orchestrate: Leg 1 → Hold → Leg 2 → Cleanup ──
    return (async () => {
      // Leg 1: from → via
      await animateLeg(from, via, leg1Duration, scaleFrom, scalePeak);
      if (this._cancelled || wrapper.destroyed) {
        this._cleanupTraveler(travelerData, wrapper);
        return;
      }

      // Hold at center
      await this._delay(holdDuration);
      if (this._cancelled || wrapper.destroyed) {
        this._cleanupTraveler(travelerData, wrapper);
        return;
      }

      // Leg 2: via → to
      await animateLeg(via, to, leg2Duration, scalePeak, scaleTo);

      // Cleanup
      this._cleanupTraveler(travelerData, wrapper);

      onArrival?.();
    })();
  }

  /**
   * Cleanup helper for travelTwoLeg — removes traveler entry and destroys wrapper.
   */
  _cleanupTraveler(travelerData, wrapper) {
    const idx = this._activeTravelers.indexOf(travelerData);
    if (idx !== -1) this._activeTravelers.splice(idx, 1);

    if (travelerData.trailEmitter) {
      travelerData.trailEmitter.target = null;
      FXManager.killTrail(travelerData.trailEmitter);
      travelerData.trailEmitter = null;
    }
    if (travelerData.tickerFn) {
      PIXI.Ticker.shared.remove(travelerData.tickerFn);
      travelerData.tickerFn = null;
    }

    if (wrapper && !wrapper.destroyed) {
      wrapper.destroy({ children: true });
    }
  }

  // ── Batch Methods ─────────────────────────────────────────────────────────

  /**
   * Fire multiple travelers simultaneously and resolve when all arrive.
   */
  async travelAll(travelerConfigs) {
    if (this._cancelled) return;
    await Promise.all(travelerConfigs.map((config) => this.travel(config)));
  }

  /**
   * Fire multiple trail-enhanced travelers with staggered launches.
   * Similar to RC's TravelOrchestrator pattern.
   *
   * @param {Array<Object>} configs - Array of travelWithTrail params
   * @param {Object} [options]
   * @param {number} [options.staggerDelay=100] - Delay between launches (ms)
   * @param {Function} [options.onEachArrival] - Called per arrival with (config, index)
   * @returns {Promise<void>}
   */
  async travelAllWithTrails(configs, options = {}) {
    if (this._cancelled || !configs || configs.length === 0) return;

    const { staggerDelay = 100, onEachArrival } = options;


    const promises = [];
    for (let i = 0; i < configs.length; i++) {
      if (this._cancelled) return;

      const config = configs[i];
      const idx = i;

      const p = this.travelWithTrail({
        ...config,
        onArrival: () => {
          config.onArrival?.();
          onEachArrival?.(config, idx);
        },
      });
      promises.push(p);

      // Stagger delay between launches (except after last)
      if (i < configs.length - 1 && staggerDelay > 0) {
        await this._delay(staggerDelay);
      }
    }

    await Promise.all(promises);
  }

  // ── Utility ───────────────────────────────────────────────────────────────

  _delay(ms) {
    if (this._cancelled) return Promise.resolve();
    return new Promise((resolve) => {
      const tid = new TickerTimeout(() => {
        this._pendingTimers.delete(tid);
        resolve();
      }, ms);
      this._pendingTimers.add(tid);
    });
  }

  // ── Resize ────────────────────────────────────────────────────────────────

  resize(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
  }

  // ── Cancel / Cleanup ──────────────────────────────────────────────────────

  cancel() {
    this._cancelled = true;

    // Clear all pending delay timers
    for (const tid of this._pendingTimers) {
      if (tid && typeof tid.clear === "function") tid.clear();
    }
    this._pendingTimers.clear();

    for (const entry of this._activeTravelers) {
      if (entry.tickerFn) {
        PIXI.Ticker.shared.remove(entry.tickerFn);
      }
      // Kill trail immediately on cancel (no fade-out)
      if (entry.trailEmitter) {
        entry.trailEmitter.target = null;
        FXManager.killTrail(entry.trailEmitter);
        entry.trailEmitter = null;
      }
      // Destroy wrapper container (contains text or spine clone)
      if (entry.container && !entry.container.destroyed) {
        if (entry.container.parent) entry.container.parent.removeChild(entry.container);
        entry.container.destroy({ children: true });
      } else if (entry.textObj && !entry.textObj.destroyed) {
        if (entry.textObj.parent) entry.textObj.parent.removeChild(entry.textObj);
        entry.textObj.destroy();
      }
      entry.resolve?.();
    }
    this._activeTravelers = [];
  }

  destroy() {
    this.cancel();
    this.container = null;
  }
}

export default PotCollectionValueTraveler;
